// src/AboutPage.jsx
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Building2, Users, Award, Shield, Target, Zap, Heart, CheckCircle, Calendar, Factory } from 'lucide-react';

const AboutPage = () => {
  const statsRef = useRef(null);
  const valuesRef = useRef(null);
  const timelineRef = useRef(null);
  
  const isStatsInView = useInView(statsRef, { once: true, threshold: 0.2 });
  const isValuesInView = useInView(valuesRef, { once: true, threshold: 0.2 });
  const isTimelineInView = useInView(timelineRef, { once: true, threshold: 0.2 });

  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stats = [
    { icon: Building2, value: "20+", label: "Yıllık Tecrübe", color: "text-brand-orange" },
    { icon: Factory, value: "1000+", label: "Proje Tamamlandı", color: "text-brand-navy" },
    { icon: Users, value: "50+", label: "Uzman Ekip", color: "text-brand-orange" },
    { icon: Award, value: "100%", label: "Müşteri Memnuniyeti", color: "text-brand-navy" }
  ];

  const values = [
    {
      icon: Shield,
      title: "Kalite Güvencesi",
      description: "TS EN 206-1 standardına uygun, tam otomatik bilgisayar kontrollü üretim.",
      gradient: "from-brand-orange to-orange-400"
    },
    {
      icon: Target,
      title: "Hedef Odaklılık",
      description: "Müşteri memnuniyetini merkeze alarak sürekli gelişim ve yenilikçilik.",
      gradient: "from-brand-navy to-blue-600"
    },
    {
      icon: Zap,
      title: "Hızlı Teslimat",
      description: "Projenizin zamanında tamamlanması için hızlı ve güvenilir teslimat.",
      gradient: "from-brand-gray to-gray-600"
    },
    {
      icon: Heart,
      title: "Güvenilirlik",
      description: "Sektördeki tecrübemizle projelerinize değer katıyor, güven veriyoruz.",
      gradient: "from-brand-orange to-red-500"
    }
  ];

  const timeline = [
    {
      year: "2000",
      title: "Kuruluş",
      description: "Onur Beton, kalite ve güven ilkesiyle sektöre adım attı."
    },
    {
      year: "2005",
      title: "Teknoloji Yatırımı",
      description: "Tam otomatik bilgisayar kontrollü beton santrali kuruldu."
    },

    {
      year: "2015",
      title: "Kapasite Artışı",
      description: "Üretim kapasitesi %200 artırılarak bölgenin lideri oldu."
    },
    {
      year: "2020",
      title: "Dijital Dönüşüm",
      description: "Dijital sipariş ve takip sistemi devreye alındı."
    },
    {
      year: "2024",
      title: "Sürdürülebilirlik",
      description: "Çevre dostu üretim süreçleri ve sürdürülebilir beton çözümleri."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-32 h-32 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-48 h-48 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Building2 className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Hakkımızda
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Kalite ve güven ilkesiyle 20+ yıldır sektörde lider konumda olan
            <span className="text-brand-orange font-semibold"> Onur İnşaat ve Beton</span>, 
            projelerinize değer katıyor.
          </motion.p>
          

        </div>
      </section>



      {/* Timeline Section */}
      <section ref={timelineRef} className="py-20 bg-brand-navy text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-brand-orange rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isTimelineInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Tarihçemiz</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Kuruluşumuzdan bugüne kadar geçen süreçteki önemli kilometre taşlarımız
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isTimelineInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
              >
                {/* Timeline line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-brand-orange opacity-30"></div>
                
                {/* Year badge */}
                <motion.div 
                  whileHover={{ scale: 1.1 }}
                  className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-brand-orange rounded-full flex items-center justify-center z-10 shadow-lg"
                >
                  <Calendar className="w-6 h-6 text-white" />
                </motion.div>

                {/* Content */}
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <motion.div 
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <h3 className="text-2xl font-bold text-brand-orange mb-2">{item.year}</h3>
                    <h4 className="text-xl font-semibold mb-3">{item.title}</h4>
                    <p className="text-gray-300 leading-relaxed">{item.description}</p>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-brand-navy to-blue-900 rounded-3xl p-8 text-white relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 w-24 h-24 bg-brand-orange opacity-20 rounded-full"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-orange rounded-2xl mb-6">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-6">Misyonumuz</h3>
                <p className="text-lg leading-relaxed text-gray-200">
                  Kalite ve güven ilkesiyle hizmet veren, TS EN 206-1 standardına uygun, 
                  müşteri memnuniyetini merkeze alan, sürekli gelişim ve yenilikçilik 
                  prensibiyle sektörde lider konuma gelmek. Her projede en yüksek 
                  standartları hedefleyerek güvenilir çözümler sunmak.
                </p>
              </div>
            </motion.div>

            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-brand-orange to-orange-600 rounded-3xl p-8 text-white relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 w-24 h-24 bg-white opacity-20 rounded-full"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6">
                  <Zap className="w-8 h-8 text-brand-orange" />
                </div>
                <h3 className="text-3xl font-bold mb-6">Vizyonumuz</h3>
                <p className="text-lg leading-relaxed text-orange-100">
                  Türkiye'nin öncü hazır beton üreticisi olarak, çevre dostu ve 
                  sürdürülebilir teknolojilerle sektöre yön vermek. İnovasyon ve 
                  kaliteden ödün vermeden, geleceğin şehirlerini inşa etmekte 
                  güvenilir partner olmak.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;